// Compute "closest services" for any locality slug:
//   - nearest service town (with population/shops/services)
//   - nearest hospital or health service
//   - nearest school (any sector/level)
//
// Distances are great-circle (haversine) in km from the locality's town-point
// centroid in towns.json. Schools without their own coordinate fall back to
// the centroid of the locality they sit in. Self-matches are excluded for
// nearest town (so Margaret River's "nearest service town" is Cowaramup, not
// Margaret River itself), but kept for hospitals and schools — a locality
// that *has* a hospital or school IS its own closest.

import townsRaw from './towns.json';
import { SCHOOLS, type SchoolEntry } from './schools';
import { HOSPITALS, type Hospital } from './hospitals';
import { LOCALITIES, type LocalityEntry } from './localities';

interface TownFeature {
  properties: { name: string; name_upper: string };
  geometry: { coordinates: number[] };
}

const TOWN_BY_UPPER = new Map<string, { lat: number; lon: number; name: string }>();
for (const f of (townsRaw as unknown as { features: TownFeature[] }).features) {
  const [lon, lat] = f.geometry.coordinates;
  TOWN_BY_UPPER.set(f.properties.name_upper, { lat, lon, name: f.properties.name });
}

/**
 * "Service towns" — localities with meaningful retail, fuel, schools and
 * usually a health service. These are the places people drive to. Curated
 * because not every gazetted locality is a real town.
 */
const SERVICE_TOWNS_UPPER = new Set<string>([
  'MARGARET RIVER',
  'AUGUSTA',
  'COWARAMUP',
  'WITCHCLIFFE',
  'KARRIDALE',
  'GRACETOWN',
  'PREVELLY',
  'MANJIMUP',
  'PEMBERTON',
  'NORTHCLIFFE',
  'WALPOLE',
  'QUINNINUP',
  'JARDEE',
  'BRIDGETOWN',
  'GREENBUSHES',
  'BALINGUP',
  'BOYUP BROOK',
  'DINNINUP',
  'MAYANUP',
  'NANNUP',
  'DENMARK',
  'PEACEFUL BAY',
  'NORNALUP',
  'KIRUP',
  'MULLALYUP',
]);

function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export interface NearestTown {
  name: string;
  distanceKm: number;
  slug: string | null;
}

export interface NearestHospital {
  hospital: Hospital;
  distanceKm: number;
  /** True if the hospital sits in the same locality as the page. */
  inSameLocality: boolean;
}

export interface NearestSchool {
  school: SchoolEntry;
  distanceKm: number;
  inSameLocality: boolean;
}

export interface ClosestServices {
  /** Centroid the calculation is anchored to. Null when locality has no point. */
  origin: { lat: number; lon: number } | null;
  nearestTown: NearestTown | null;
  nearestHospital: NearestHospital | null;
  nearestSchool: NearestSchool | null;
}

const LOCALITY_BY_NAME_UPPER = new Map<string, LocalityEntry>();
for (const l of LOCALITIES) LOCALITY_BY_NAME_UPPER.set(l.upperName, l);

function originFor(entry: LocalityEntry): { lat: number; lon: number } | null {
  const t = TOWN_BY_UPPER.get(entry.upperName);
  return t ? { lat: t.lat, lon: t.lon } : null;
}

export function closestServicesFor(entry: LocalityEntry): ClosestServices {
  const origin = originFor(entry);
  if (!origin) {
    return { origin: null, nearestTown: null, nearestHospital: null, nearestSchool: null };
  }

  // Nearest service town (excluding self).
  let nearestTown: NearestTown | null = null;
  for (const upper of SERVICE_TOWNS_UPPER) {
    if (upper === entry.upperName) continue;
    const t = TOWN_BY_UPPER.get(upper);
    if (!t) continue;
    const d = haversineKm(origin.lat, origin.lon, t.lat, t.lon);
    if (!nearestTown || d < nearestTown.distanceKm) {
      const matched = LOCALITY_BY_NAME_UPPER.get(upper);
      nearestTown = { name: t.name, distanceKm: d, slug: matched?.slug ?? null };
    }
  }

  // Nearest hospital.
  let nearestHospital: NearestHospital | null = null;
  for (const h of HOSPITALS) {
    const d = haversineKm(origin.lat, origin.lon, h.lat, h.lon);
    if (!nearestHospital || d < nearestHospital.distanceKm) {
      nearestHospital = {
        hospital: h,
        distanceKm: d,
        inSameLocality: h.town.toUpperCase() === entry.name.toUpperCase(),
      };
    }
  }

  // Nearest school — use the locality centroid the school sits in as its point.
  let nearestSchool: NearestSchool | null = null;
  for (const s of SCHOOLS) {
    if (s.shireId == null) continue; // skip region-wide TAFE network entries
    const locUpper = s.locality.toUpperCase();
    const t = TOWN_BY_UPPER.get(locUpper);
    if (!t) continue;
    const d = haversineKm(origin.lat, origin.lon, t.lat, t.lon);
    if (!nearestSchool || d < nearestSchool.distanceKm) {
      nearestSchool = {
        school: s,
        distanceKm: d,
        inSameLocality: locUpper === entry.upperName,
      };
    }
  }

  return { origin, nearestTown, nearestHospital, nearestSchool };
}
